import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Clock, User, BookOpen, Loader2, CheckCircle2, XCircle, Users, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import apiService from '@/services/api';
import { useNavigate } from 'react-router-dom';

// Primary School Timetable - Simple daily structure
const PRIMARY_PERIODS = [
  { number: 1, name: "Morning Session 1", start: "07:30", end: "09:00", subject: "Language & Communication" },
  { number: 2, name: "Morning Session 2", start: "09:30", end: "11:00", subject: "Mathematics" },
  { number: 3, name: "Afternoon Session 1", start: "12:30", end: "14:00", subject: "Science / Living in Community" },
  { number: 4, name: "Afternoon Session 2", start: "14:00", end: "15:00", subject: "Arts / Physical Education" },
];

const PERIODS = [
  { number: 1, start: "07:30", end: "08:30" },
  { number: 2, start: "08:30", end: "09:30" },
  // Break: 9:30 - 9:45
  { number: 3, start: "09:45", end: "10:45" },
  { number: 4, start: "10:45", end: "11:45" },
  // Lunch: 11:45 - 1:00 PM
  { number: 5, start: "13:00", end: "13:50" },
  { number: 6, start: "13:50", end: "14:40" },
  { number: 7, start: "14:40", end: "15:30" },
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Mapping for French day names to English (for French schools)
const DAY_MAPPING = {
  "Monday": ["Monday", "Lundi"],
  "Tuesday": ["Tuesday", "Mardi"],
  "Wednesday": ["Wednesday", "Mercredi"],
  "Thursday": ["Thursday", "Jeudi"],
  "Friday": ["Friday", "Vendredi"]
};

export default function TimetablePage() {
  const [loading, setLoading] = useState(true);
  const [timetables, setTimetables] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [mySchedule, setMySchedule] = useState(null);
  
  // Primary school attendance state
  const [primaryStudents, setPrimaryStudents] = useState([]);
  const [primaryAttendance, setPrimaryAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const isPrincipal = currentUser.role === 'principal' || currentUser.role === 'admin';
  const isPrimarySchool = currentUser.school_type === 'primary';
  const primaryYear = parseInt((/\b(?:class|year)\s*([1-6])\b/i.exec(`${currentUser.department || ''} ${currentUser.position || ''}`) || [])[1], 10);
  const hasLiteracyProgram = isPrimarySchool && primaryYear >= 1 && primaryYear <= 3;
  const primaryPeriods = hasLiteracyProgram
    ? [{ number: 1, name: 'Weekly Literacy Program · click for today\'s plan', start: '07:30', end: '09:45', subject: 'Literacy Program', literacy: true },
       { number: 2, name: 'Morning Session 2', start: '09:45', end: '11:00', subject: 'Mathematics' }, ...PRIMARY_PERIODS.slice(2)]
    : PRIMARY_PERIODS;

  // Fetch primary school students for attendance
  useEffect(() => {
    const fetchPrimaryStudents = async () => {
      if (!isPrimarySchool || currentUser.role !== 'teacher') return;
      
      try {
        const classes = await apiService.getTeacherClasses();
        if (classes && classes.length > 0) {
          const classId = classes[0].id;
          const response = await fetch(
            `/api/classes/${classId}/students`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              }
            }
          );
          if (response.ok) {
            const students = await response.json();
            setPrimaryStudents(students);
            
            // Load saved attendance from localStorage
            const savedKey = `primary_attendance_${classId}_${selectedDate}`;
            const saved = localStorage.getItem(savedKey);
            if (saved) {
              setPrimaryAttendance(JSON.parse(saved));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching primary students:', error);
      }
    };
    
    fetchPrimaryStudents();
  }, [isPrimarySchool, currentUser.role, selectedDate]);

  // Toggle primary attendance
  const togglePrimaryAttendance = (studentId, status) => {
    setPrimaryAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  // Save primary attendance
  const savePrimaryAttendance = async () => {
    try {
      const classes = await apiService.getTeacherClasses();
      if (classes && classes.length > 0) {
        const classId = classes[0].id;
        const savedKey = `primary_attendance_${classId}_${selectedDate}`;
        localStorage.setItem(savedKey, JSON.stringify(primaryAttendance));
        toast.success('Attendance saved successfully!');
      }
    } catch (error) {
      toast.error('Failed to save attendance');
    }
  };

  useEffect(() => {
    const fetchTimetables = async () => {
      try {
        setLoading(true);
        
        // Get school ID from either school_id or schoolId field
        const schoolId = currentUser.school_id || currentUser.schoolId;
        
        if (!schoolId) {
          console.error('No school ID found for user');
          setLoading(false);
          return;
        }
        
        // Only fetch all timetables if user is principal/admin
        if (isPrincipal) {
          const response = await apiService.request(`/api/timetable/school/${schoolId}`);
          setTimetables(response.timetables || []);
          
          // Set first class as selected
          if (response.timetables && response.timetables.length > 0) {
            setSelectedClass(response.timetables[0].class_name);
            setCurrentSchedule(response.timetables[0].schedule_data);
          }
        }
        
        // Fetch teacher's personal timetable (only for teachers)
        if (currentUser.role === 'teacher') {
          try {
            const myTimetableResponse = await apiService.request('/api/timetable/my-timetable');
            setMySchedule(myTimetableResponse.schedule);
          } catch (err) {
            console.error('Error fetching personal schedule:', err);
            // Continue even if personal schedule fails
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching timetables:', error);
        setLoading(false);
      }
    };
    
    fetchTimetables();
  }, [currentUser.school_id, currentUser.schoolId, currentUser.role, isPrincipal]);

  const handleClassChange = (className) => {
    setSelectedClass(className);
    const timetable = timetables.find(t => t.class_name === className);
    setCurrentSchedule(timetable?.schedule_data || null);
  };

  const renderPeriodCell = (day, periodNumber) => {
    if (!currentSchedule) return null;
    
    // Try both English and French day names
    const possibleDays = DAY_MAPPING[day] || [day];
    let periods = null;
    
    for (const possibleDay of possibleDays) {
      if (currentSchedule[possibleDay]) {
        periods = currentSchedule[possibleDay];
        break;
      }
    }
    
    if (!periods) return null;
    
    // Find ALL periods for this time slot (to detect conflicts)
    const matchingPeriods = periods.filter(p => {
      if (p.is_double) {
        return periodNumber >= p.period_start && periodNumber <= p.period_end;
      }
      return p.period === periodNumber;
    });
    
    if (matchingPeriods.length === 0) {
      return <div className="text-xs text-gray-400">-</div>;
    }
    
    // Check if multiple matches are the same double period (not a real conflict)
    const uniquePeriods = matchingPeriods.reduce((acc, p) => {
      // For double periods, use period_start as key to dedupe
      const key = p.is_double 
        ? `${p.subject}-${p.teacher_email}-${p.period_start}` 
        : `${p.subject}-${p.teacher_email}-${p.period}`;
      if (!acc.find(x => {
        const xKey = x.is_double 
          ? `${x.subject}-${x.teacher_email}-${x.period_start}` 
          : `${x.subject}-${x.teacher_email}-${x.period}`;
        return xKey === key;
      })) {
        acc.push(p);
      }
      return acc;
    }, []);
    
    // If there are multiple UNIQUE periods (real conflict), show them all with warning
    if (uniquePeriods.length > 1) {
      return (
        <div className="space-y-1">
          {uniquePeriods.map((periodData, idx) => (
            <div key={idx} className="p-2 bg-red-50 border-l-4 border-red-500 rounded">
              <div className="font-semibold text-sm text-red-900">{periodData.subject}</div>
              <div className="text-xs text-red-700 mt-1">
                <User className="w-3 h-3 inline mr-1" />
                {periodData.teacher_email?.split('@')[0]}
              </div>
              <div className="text-xs text-red-600 font-medium mt-1">
                ⚠️ Conflict
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // Single match - render normally
    const periodData = uniquePeriods[0];
    
    // Check if this is the first cell of a double period
    if (periodData.literacy_program && periodNumber === periodData.period_start) {
      return (
        <a href="/dashboard/literacy-program" className="block p-2 bg-rose-50 border-l-4 border-rose-500 rounded hover:bg-rose-100 transition-colors" data-testid="timetable-literacy-cell">
          <div className="font-semibold text-sm text-rose-900">{periodData.subject}</div>
          <div className="text-xs text-rose-700 mt-1">07:30–09:45 · click for today's plan</div>
        </a>
      );
    }
    if (periodData.is_double && periodNumber === periodData.period_start) {
      return (
        <div className="p-2 bg-blue-50 border-l-4 border-blue-500 rounded">
          <div className="font-semibold text-sm text-blue-900">{periodData.subject}</div>
          <div className="text-xs text-blue-700 mt-1">
            <User className="w-3 h-3 inline mr-1" />
            {periodData.teacher_email?.split('@')[0]}
          </div>
          <div className="text-xs text-blue-600 font-medium mt-1">
            Double Period
          </div>
        </div>
      );
    }
    
    // Skip rendering if this is the second cell of a double period
    if (periodData.is_double && periodNumber > periodData.period_start) {
      return null;
    }
    
    // Single period
    return (
      <div className="p-2 bg-green-50 border-l-4 border-green-500 rounded">
        <div className="font-semibold text-sm text-green-900">{periodData.subject}</div>
        <div className="text-xs text-green-700 mt-1">
          <User className="w-3 h-3 inline mr-1" />
          {periodData.teacher_email?.split('@')[0]}
        </div>
      </div>
    );
  };

  const renderMyScheduleCell = (day, periodNumber) => {
    if (!mySchedule) return null;
    
    // Try both English and French day names
    const possibleDays = DAY_MAPPING[day] || [day];
    let periods = null;
    
    for (const possibleDay of possibleDays) {
      if (mySchedule[possibleDay]) {
        periods = mySchedule[possibleDay];
        break;
      }
    }
    
    if (!periods) return null;
    
    // Find ALL periods for this time slot (teacher may have multiple classes)
    const matchingPeriods = periods.filter(p => {
      if (p.is_double) {
        return periodNumber >= p.period_start && periodNumber <= p.period_end;
      }
      return p.period === periodNumber;
    });
    
    if (matchingPeriods.length === 0) {
      return <div className="text-xs text-gray-400">Free</div>;
    }
    
    // Check for double periods - only show on first period
    const doublePeriod = matchingPeriods.find(p => p.is_double);
    if (doublePeriod && periodNumber > doublePeriod.period_start) {
      return null; // Skip second cell of double period
    }
    
    // Render all classes for this period
    return (
      <div className="space-y-1">
        {matchingPeriods.map((periodData, idx) => {
          const isDouble = periodData.is_double && periodNumber === periodData.period_start;
          return (
            <div key={idx} className="p-2 bg-purple-50 border-l-4 border-purple-500 rounded">
              <div className="font-semibold text-sm text-purple-900">{periodData.subject}</div>
              <div className="text-xs text-purple-700 mt-1 font-medium">
                <BookOpen className="w-3 h-3 inline mr-1" />
                {periodData.class || periodData.class_name || 'Class'}
              </div>
              {isDouble && (
                <div className="text-xs text-purple-600 font-medium mt-1">
                  Double Period
                </div>
              )}
              {periodData.room && (
                <div className="text-xs text-purple-500 mt-1">
                  📍 {periodData.room}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-8 h-8 text-blue-600" />
            {isPrimarySchool ? 'Daily Timetable & Attendance' : (isPrincipal ? 'School Timetable' : 'My Teaching Schedule')}
          </h1>
          <p className="text-gray-600 mt-1">
            {isPrimarySchool ? 'Primary School - Track daily sessions and attendance' : `${new Date().getFullYear()} Academic Year`}
          </p>
        </div>
      </div>

      {/* PRIMARY SCHOOL VIEW */}
      {isPrimarySchool && currentUser.role === 'teacher' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Timetable Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Today's Schedule
              </CardTitle>
              <CardDescription>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {primaryPeriods.map((period) => (
                  <div key={period.number} onClick={() => period.literacy && navigate('/dashboard/literacy-program')} data-testid={period.literacy ? 'primary-literacy-session' : undefined}
                    className={`flex items-center gap-4 p-4 rounded-lg border ${period.literacy ? 'bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200 cursor-pointer hover:shadow-md transition-shadow' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
                    <div className="w-16 text-center">
                      <div className="text-xs text-gray-500">{period.start}</div>
                      <div className="text-xs text-gray-400">to</div>
                      <div className="text-xs text-gray-500">{period.end}</div>
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold ${period.literacy ? 'text-rose-900' : 'text-blue-900'}`}>{period.subject}</div>
                      <div className={`text-sm ${period.literacy ? 'text-rose-600' : 'text-blue-600'}`}>{period.name}</div>
                    </div>
                    <Badge variant="outline" className="bg-white">
                      Session {period.number}
                    </Badge>
                  </div>
                ))}
              </div>
              
              {/* Break/Lunch markers */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                  <span className="font-medium">☕ Morning Break:</span> {hasLiteracyProgram ? '9:45 - 10:00' : '9:00 - 9:30'}
                </div>
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                  <span className="font-medium">🍽️ Lunch Break:</span> 11:00 - 12:30
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Marking Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Daily Attendance
                  </CardTitle>
                  <CardDescription>
                    Mark attendance for {selectedDate}
                  </CardDescription>
                </div>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </CardHeader>
            <CardContent>
              {primaryStudents.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex gap-4">
                      <span className="text-sm">
                        <span className="font-medium text-green-600">
                          {Object.values(primaryAttendance).filter(s => s === 'present').length}
                        </span> Present
                      </span>
                      <span className="text-sm">
                        <span className="font-medium text-red-600">
                          {Object.values(primaryAttendance).filter(s => s === 'absent').length}
                        </span> Absent
                      </span>
                      <span className="text-sm">
                        <span className="font-medium text-gray-500">
                          {primaryStudents.length - Object.keys(primaryAttendance).length}
                        </span> Unmarked
                      </span>
                    </div>
                    <Button size="sm" onClick={savePrimaryAttendance}>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {primaryStudents.map((student, idx) => (
                      <div key={student.id || idx} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-700">
                            {idx + 1}
                          </div>
                          <span className="font-medium">{student.name}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={primaryAttendance[student.id] === 'present' ? 'default' : 'outline'}
                            className={primaryAttendance[student.id] === 'present' ? 'bg-green-500 hover:bg-green-600' : ''}
                            onClick={() => togglePrimaryAttendance(student.id, 'present')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={primaryAttendance[student.id] === 'absent' ? 'default' : 'outline'}
                            className={primaryAttendance[student.id] === 'absent' ? 'bg-red-500 hover:bg-red-600' : ''}
                            onClick={() => togglePrimaryAttendance(student.id, 'absent')}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No students found</p>
                  <p className="text-sm mt-1">Students will appear once assigned to your class</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : isPrincipal ? (
        /* Principal View - Master Timetable with Class Selector */
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>School Timetable</CardTitle>
              <Select value={selectedClass} onValueChange={handleClassChange}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent>
                  {timetables.map((timetable) => (
                    <SelectItem key={timetable.id} value={timetable.class_name}>
                      {timetable.class_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>

          <CardContent>
            {currentSchedule ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left font-semibold">
                        <Clock className="w-4 h-4 inline mr-2" />
                        Period
                      </th>
                      {DAYS.map(day => (
                        <th key={day} className="border p-3 text-center font-semibold">
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERIODS.map((period) => (
                      <tr key={period.number} className="hover:bg-gray-50">
                        <td className="border p-3">
                          <div className="font-medium">Period {period.number}</div>
                          <div className="text-xs text-gray-500">
                            {period.start} - {period.end}
                          </div>
                        </td>
                        {DAYS.map(day => (
                          <td key={`${day}-${period.number}`} className="border p-2">
                            {renderPeriodCell(day, period.number)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-yellow-50">
                      <td className="border p-3 font-semibold" colSpan={6}>
                        <div className="flex items-center justify-center gap-4">
                          <span>Break: 9:30 - 10:00</span>
                          <span>|</span>
                          <span>Lunch: 12:00 - 1:00</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {timetables.length === 0 
                  ? "No timetables available for this school"
                  : "Select a class to view timetable"}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Teachers see only their schedule - no tabs */
        <div className="space-y-4">
          {/* Subject Summary Card */}
          {mySchedule && (
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Teaching Assignment</h3>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Extract unique subjects from schedule
                        const subjects = new Set();
                        Object.values(mySchedule).forEach(daySchedule => {
                          daySchedule.forEach(period => {
                            if (period.subject) subjects.add(period.subject);
                          });
                        });
                        return Array.from(subjects).map(subject => (
                          <span key={subject} className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-medium">
                            {subject}
                          </span>
                        ));
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-purple-600">
                      {(() => {
                        // Count total periods
                        let count = 0;
                        Object.values(mySchedule).forEach(daySchedule => {
                          count += daySchedule.length;
                        });
                        return count;
                      })()}
                    </div>
                    <div className="text-sm text-gray-600">Periods/Week</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>My Teaching Schedule</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Showing only your assigned teaching periods</p>
            </CardHeader>

            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : mySchedule ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left font-semibold">
                        <Clock className="w-4 h-4 inline mr-2" />
                        Period
                      </th>
                      {DAYS.map(day => (
                        <th key={day} className="border p-3 text-center font-semibold">
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERIODS.map((period) => (
                      <tr key={period.number} className="hover:bg-gray-50">
                        <td className="border p-3">
                          <div className="font-medium">Period {period.number}</div>
                          <div className="text-xs text-gray-500">
                            {period.start} - {period.end}
                          </div>
                        </td>
                        {DAYS.map(day => (
                          <td key={`${day}-${period.number}`} className="border p-2">
                            {renderMyScheduleCell(day, period.number)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-yellow-50">
                      <td className="border p-3 font-semibold" colSpan={6}>
                        <div className="flex items-center justify-center gap-4">
                          <span>Break: 9:30 - 10:00</span>
                          <span>|</span>
                          <span>Lunch: 12:00 - 1:00</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No schedule data available
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm">Single Period</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span className="text-sm">Double Period (Practical Subjects)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded"></div>
              <span className="text-sm">My Teaching Periods</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
